import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import type { AnyValue } from "@/lib/rowTypes";

export type ActionStatus = "Suggested" | "Planned" | "Completed" | "Snoozed" | "Dismissed";
export type ActionPriority = "Low" | "Medium" | "High";

export interface ParentActionItem {
  id: string;
  parent_user_id: string;
  child_user_id: string;
  topic: string | null;
  action_title: string;
  action_reason: string | null;
  conversation_prompt: string | null;
  priority: ActionPriority;
  status: ActionStatus;
  source_type: string | null;
  source_id: string | null;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
  snoozed_until: string | null;
  dismissed_at: string | null;
}

export function useParentActions(childId: string | null) {
  const { user } = useAuth();
  const [items, setItems] = useState<ParentActionItem[]>([]);
  const [loading, setLoading] = useState(false);

  const refetch = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    let q = supabase.from("parent_action_items").select("*").eq("parent_user_id", user.id).order("created_at", { ascending: false });
    if (childId) q = q.eq("child_user_id", childId);
    const { data, error } = await q;
    if (error) console.error("[useParentActions] load failed:", error);
    setItems(((data) || []) as ParentActionItem[]);
    setLoading(false);
  }, [user, childId]);

  useEffect(() => { void refetch(); }, [refetch]);

  const create = useCallback(async (input: Partial<ParentActionItem> & { child_user_id: string; action_title: string }) => {
    if (!user) return;
    // Client-side dedup: never create a second OPEN card with the same
    // child + action_title + source_type. Respect resolved (Completed /
    // Dismissed / Snoozed) cards by not regenerating against them either.
    const sourceType = input.source_type ?? null;
    const existing = items.find(
      (it) =>
        it.child_user_id === input.child_user_id &&
        it.action_title === input.action_title &&
        (it.source_type ?? null) === sourceType,
    );
    if (existing) {
      // If still open, just bump updated_at and bail — no new row.
      if (existing.status === "Suggested" || existing.status === "Planned") {
        await supabase
          .from("parent_action_items")
          .update({ updated_at: new Date().toISOString() })
          .eq("id", existing.id);
        return;
      }
      // Snoozed and not yet expired → do not regenerate.
      if (existing.status === "Snoozed" && existing.snoozed_until) {
        if (new Date(existing.snoozed_until).getTime() > Date.now()) return;
      }
      // Completed or Dismissed within the last 14 days → do not regenerate.
      const resolvedAt = existing.completed_at ?? existing.dismissed_at;
      if (resolvedAt) {
        const ageDays = (Date.now() - new Date(resolvedAt).getTime()) / 86400000;
        if (ageDays < 14) return;
      }
    }
    const { error } = await supabase.from("parent_action_items").insert({
      parent_user_id: user.id,
      child_user_id: input.child_user_id,
      action_title: input.action_title,
      action_reason: input.action_reason ?? null,
      conversation_prompt: input.conversation_prompt ?? null,
      topic: input.topic ?? null,
      priority: input.priority ?? "Medium",
      status: input.status ?? "Suggested",
      source_type: sourceType,
      source_id: input.source_id ?? null,
    });
    if (error) {
      // Partial unique index can race against a concurrent insert — swallow it
      // because the existing row already satisfies the intent.
      const code = (error as { code?: string }).code;
      if (code !== "23505") {
        console.error("[useParentActions] insert failed:", error);
      }
    }
    await refetch();
  }, [user, items, refetch]);

  const updateStatus = useCallback(async (id: string, status: ActionStatus) => {
    const patch: Record<string, AnyValue> = { status };
    if (status === "Completed") patch.completed_at = new Date().toISOString();
    if (status === "Dismissed") patch.dismissed_at = new Date().toISOString();
    if (status === "Snoozed") patch.snoozed_until = new Date(Date.now() + 7 * 86400000).toISOString();
    const { error } = await supabase.from("parent_action_items").update(patch as AnyValue).eq("id", id);
    if (error) console.error("[useParentActions] update failed:", error);
    await refetch();
  }, [refetch]);

  const remove = useCallback(async (id: string) => {
    const { error } = await supabase.from("parent_action_items").delete().eq("id", id);
    if (error) console.error("[useParentActions] delete failed:", error);
    await refetch();
  }, [refetch]);

  return { items, loading, refetch, create, updateStatus, remove };
}